import { Link, useParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { useClassGallery, useToggleReaction } from './useTeacher';
import './ClassGalleryPage.css';

const REACTIONS: { code: string; emoji: string; label: string }[] = [
  { code: 'star', emoji: '🌟', label: 'great work' },
  { code: 'lightbulb', emoji: '💡', label: 'taught me something' },
  { code: 'celebrate', emoji: '🎉', label: 'love this' },
  { code: 'clap', emoji: '👏', label: 'great job' },
];

/**
 * Class gallery — `/class/:cid/gallery`.
 *
 * Approved class_only submissions in one room. Visible to teacher
 * and students; classmates can react with the four supportive
 * emojis. No comments, no DMs, no follower counts — by design.
 *
 * Reactor identity is intentionally NOT surfaced for other people's
 * tiles — counts only. The kid's *own* state (`my_reactions`) shows
 * which emojis they've clicked, so the toggle UI feels stateful.
 */
export function ClassGalleryPage(): JSX.Element {
  const { cid = '' } = useParams<{ cid: string }>();
  const { data: user, isPending: userPending } = useCurrentUser();
  const gallery = useClassGallery(cid);
  const toggle = useToggleReaction(cid);

  if (userPending) {
    return <div className="bl-cg" data-testid="cg-loading" />;
  }
  if (!user) {
    return (
      <div className="bl-cg">
        <p className="bl-cg__notice">Sign in to see your class gallery.</p>
      </div>
    );
  }

  const isTeacher = user.role === 'teacher' || user.role === 'admin';

  return (
    <div className="bl-cg">
      <header className="bl-cg__header">
        <Link to={isTeacher ? `/teacher/classrooms/${cid}` : '/studio'} className="bl-cg__back">
          {isTeacher ? '← Classroom' : '← Studio'}
        </Link>
        <h1 className="bl-cg__title">Class gallery</h1>
        <p className="bl-cg__subtitle">
          Approved work from this classroom. React to celebrate your classmates&apos; ideas.
        </p>
      </header>

      {gallery.isPending ? (
        <p className="bl-cg__notice">Loading…</p>
      ) : gallery.isError ? (
        <p className="bl-cg__notice">Couldn&apos;t load the gallery.</p>
      ) : !gallery.data || gallery.data.length === 0 ? (
        <div className="bl-cg__empty">
          <p>Nothing approved yet.</p>
          <p className="bl-cg__empty-hint">
            Once your teacher approves a project, it shows up here.
          </p>
        </div>
      ) : (
        <ul className="bl-cg__grid" data-testid="gallery-grid">
          {gallery.data.map((entry) => (
            <li key={entry.id} className="bl-cg__card">
              <div className="bl-cg__card-head">
                <span className={`bl-cg__kind bl-cg__kind--${entry.project_kind}`}>
                  {entry.project_kind}
                </span>
              </div>
              <h3 className="bl-cg__card-name">{entry.project_name}</h3>
              <p className="bl-cg__card-by">by {entry.submitter_display_name ?? 'a classmate'}</p>

              <div className="bl-cg__reactions">
                {REACTIONS.map((r) => {
                  const count = entry.reaction_counts[r.code] ?? 0;
                  const mine = entry.my_reactions.includes(r.code);
                  return (
                    <button
                      type="button"
                      key={r.code}
                      className={mine ? 'bl-cg__react bl-cg__react--mine' : 'bl-cg__react'}
                      onClick={() =>
                        toggle.mutate({
                          submissionId: entry.id,
                          code: r.code,
                          hasReacted: mine,
                        })
                      }
                      disabled={toggle.isPending}
                      title={r.label}
                      aria-label={r.label}
                      aria-pressed={mine}
                    >
                      <span className="bl-cg__react-emoji">{r.emoji}</span>
                      {count > 0 ? <span className="bl-cg__react-count">{count}</span> : null}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
